import type { ZoneInput } from '@chukua/common';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { ApiError } from '../../lib/api';
import { useToast } from '../../lib/use-toast';
import { createZone, getZone, updateZone, type Zone } from './reference-data.api';
import { referenceDataTabPath } from './reference-data-tabs';
import { useZoneKinds } from './use-zone-kinds';
import { useZonesMapTileKey } from './use-zones-map-tile-key';

/** Where the editor returns to: Reference data's Zones tab. */
export const ZONES_TAB_PATH = referenceDataTabPath('zones');

/** `zone` is null for a new zone. */
export type ZoneRecordState =
  { status: 'loading' } | { status: 'ready'; zone: Zone | null } | { status: 'forbidden' } | { status: 'not_found' } | { status: 'error' };

function toErrorState(err: unknown): ZoneRecordState {
  if (err instanceof ApiError && err.status === 403) return { status: 'forbidden' };
  if (err instanceof ApiError && err.status === 404) return { status: 'not_found' };
  return { status: 'error' };
}

function saveErrorMessage(err: unknown): string {
  return err instanceof ApiError && err.status === 409 ? 'A zone with this code already exists.' : 'Could not save — check the fields and try again.';
}

/** Mount with `key={zoneId}` — the record state is set up once per zone, not re-synced when the id changes. */
function useZoneRecord(zoneId: string | undefined): ZoneRecordState {
  const [state, setState] = useState<ZoneRecordState>(zoneId ? { status: 'loading' } : { status: 'ready', zone: null });

  useEffect(() => {
    if (!zoneId) return;
    let cancelled = false;
    getZone(zoneId)
      .then((zone) => {
        if (!cancelled) setState({ status: 'ready', zone });
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });
    return () => {
      cancelled = true;
    };
  }, [zoneId]);

  return state;
}

/**
 * A zone's own page: the zone (or none, when new), the active zone kinds for its picker and the
 * map key, plus the save. A save failure stays inline; success toasts and returns to the Zones tab.
 */
export function useZoneEditor(zoneId: string | undefined) {
  const record = useZoneRecord(zoneId);
  const zoneKinds = useZoneKinds();
  const mapTileKey = useZonesMapTileKey();
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  async function save(input: ZoneInput): Promise<void> {
    setSaveError(null);
    setSaving(true);
    try {
      await (zoneId ? updateZone(zoneId, input) : createZone(input));
    } catch (err) {
      setSaveError(saveErrorMessage(err));
      setSaving(false);
      return;
    }
    setSaving(false);
    toast.success(`${input.name} ${zoneId ? 'updated' : 'created'}`);
    navigate(ZONES_TAB_PATH);
  }

  const activeKinds = zoneKinds.state.status === 'loaded' ? zoneKinds.state.kinds.filter((kind) => kind.isActive) : [];

  return { record, kindsStatus: zoneKinds.state.status, activeKinds, mapTileKey, saveError, saving, save };
}
